import React from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { useTheme } from 'styled-components'
import { ITheme } from 'theme/interface'
import * as S from './styles'

interface AreaLineChartProps<T> {
  data: T[]
  xKey: keyof T & string
  yKey: keyof T & string
  height?: number
  yDomain?: [number | string, number | string]
  yTicks?: number[]
  tooltipValueLabel?: string
  tooltipFormatter?: (value: number) => string
  yTickFormatter?: (value: number) => string
  color?: string
  gradientStart?: string
  gradientEnd?: string
}

const AreaLineChart = <T,>({
  data,
  xKey,
  yKey,
  height = 220,
  yDomain,
  yTicks,
  tooltipValueLabel,
  tooltipFormatter,
  yTickFormatter,
  color: colorProp,
  gradientStart,
  gradientEnd,
}: AreaLineChartProps<T>) => {
  const theme = useTheme() as ITheme
  const color: string = colorProp ?? theme?.colors?.primaryDark ?? '#FFA947'

  return (
    <S.Wrapper height={height}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{ top: 8, right: 8, left: yTickFormatter ? 16 : -16, bottom: 0 }}
        >
          <defs>
            <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={gradientStart ?? color} stopOpacity={gradientStart ? 1 : 0.25} />
              <stop offset="100%" stopColor={gradientEnd ?? color} stopOpacity={gradientEnd ? 1 : 0.02} />
            </linearGradient>
          </defs>

          <CartesianGrid vertical={false} stroke="#f0f0f0" />

          <XAxis
            dataKey={xKey}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12 }}
          />

          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12 }}
            domain={yDomain}
            ticks={yTicks}
            tickFormatter={yTickFormatter}
            width={yTickFormatter ? 80 : 60}
          />

          <Tooltip
            contentStyle={{
              borderRadius: 8,
              border: '1px solid #f0f0f0',
              fontSize: 12,
            }}
            formatter={(value: number, name: string) => [
              tooltipFormatter ? tooltipFormatter(value) : value,
              tooltipValueLabel ?? name,
            ]}
          />

          <Area
            type="monotone"
            dataKey={yKey}
            stroke={color}
            strokeWidth={2}
            fill="url(#areaGradient)"
            dot={{ r: 4, fill: '#F7F7F7', fillOpacity: 1, stroke: color, strokeWidth: 2 }}
            activeDot={{ r: 6, fill: '#F7F7F7', fillOpacity: 1, stroke: color, strokeWidth: 2 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </S.Wrapper>
  )
}

export default AreaLineChart
